<template>
  <div class="c-form-item-multi-area">
    <el-cascader
      ref="cascaderRef"
      v-model="innerModelValue"
      :options="areaOptions"
      :props="cascaderProps"
      :show-all-levels="showAllLevels"
      :filterable="filterable"
      :collapse-tags="collapseTags"
      :clearable="clearable"
      style="width: 100%"
      @change="handleChange"
      @visible-change="handleVisibleChange"
    />
  </div>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script lang="ts" setup>
import { _, watch, computed, inject, ref, useCurrentAppInstance } from '@zto/zpage'
import { C_FORM_KEY } from '@zto/zpage-ui-element'
import { getAreasRef } from '../../../utils/area'

import type { GenericObject } from '@zto/zpage'

type NodeFilter = (node: any) => boolean

const props = withDefaults(
  defineProps<{
    model: GenericObject
    prop: string
    showAllLevels?: boolean
    clearable?: boolean
    filterable?: boolean
    collapseTags?: boolean
    checkStrictly?: boolean
    propsConfig?: any
    areaLevel?: number
    areaStrictly?: boolean
    isPathValue?: boolean
    disabledAll?: boolean // 禁用所有选项
    disabledValues?: any[] // 禁用的选项
    filteredValues?: any[] // 过滤掉的Id项
  }>(),
  {
    showAllLevels: true,
    filterable: false,
    clearable: true,
    disabledAll: false,
    collapseTags: true,
    checkStrictly: false,
    areaLevel: 0, // 显示options级别
    areaStrictly: false // 严格区分区域
  }
)

const cForm = inject<any>(C_FORM_KEY, null)

const app = useCurrentAppInstance()

const cascaderRef = ref<any>()

const innerModelValue = ref<string[]>([])

const innerAreas = ref<any[]>([])

innerAreas.value = _.deepClone(getAreasRef(props.areaLevel).value)

watch(
  () => props.filteredValues,
  (cur, old) => {
    if (_.deepEqual(cur || [], old || [])) return

    let _innerAreas = _.deepClone(getAreasRef(props.areaLevel).value)

    const filteredIds = (props.filteredValues || []).map(v => nodeValue(v))
    if (!filteredIds?.length) return

    if (filteredIds.length) {
      _innerAreas = _innerAreas.filter((n: any) => !filteredIds.includes(n.value))
    }

    _innerAreas.forEach((n: any) => {
      deepProcessNodes(n, null, (_n: any, _p: any) => {
        if (!_n) return
        if (!filteredIds.includes(_n.value)) return

        if (_p?.children?.length) _p.children = (_p.children || []).filter((it: any) => it !== _n)
      })
    })

    innerAreas.value = _innerAreas
  }
)

const areaOptions = computed(() => {
  const disabledIds = (props.disabledValues || []).map(v => nodeValue(v))
  const disabledAll = props.disabledAll === true

  innerAreas.value.forEach((n: any) => {
    if (disabledAll || disabledIds.includes(n.value)) {
      n.disabled = true
    }
    deepProcessNodes(n, null, (_n: any, _p: any) => {
      if (!_n) return

      if (disabledAll || disabledIds.includes(_n.value)) {
        _n.disabled = true
      }
    })
  })

  return innerAreas.value
})

const cascaderProps = computed(() => {
  return {
    multiple: true,
    checkStrictly: props.checkStrictly,
    ...props.propsConfig
  }
})

watch(
  () => innerModelValue.value,
  () => {
    if (!props.isPathValue) return
    if (_.deepEqual(innerModelValue.value, props.model[props.prop])) return

    if (props.isPathValue) {
      props.model[props.prop] = innerModelValue.value
    } else {
      props.model[props.prop] = innerModelValue.value.map(v => nodeValue(v))
    }

    if (cForm) {
      cForm.validateFields([props.prop])
    }
  }
)

watch(
  () => props.model[props.prop],
  () => {
    if (!props.isPathValue) return

    if (_.deepEqual(innerModelValue.value, props.model[props.prop])) return
    innerModelValue.value = _.deepClone(props.model[props.prop])
  },
  { immediate: true }
)

const handleChange = (checkedValue: any[]) => {
  const checkNodes: any[] = cascaderRef.value.getCheckedNodes()
  if (props.areaStrictly) {
    const newValues = checkedValue.map(it => nodeValue(it))
    const oldValues = (props.model[props.prop] || []).map((it: any) => nodeValue(it))
    const newValue = newValues.find(v => !oldValues.includes(v))
    if (newValue) {
      const newNode = checkNodes.find(it => it.value === newValue)
      if (newNode) {
        // 如果父节点选择了，则移除父节点，如果子节点有选择，则子节点清空
        const filteredNodes = getCascadeNodes(newNode, n => n.checked)
        const filteredValues = filteredNodes.map(n => n.value)
        checkNodes.forEach(n => {
          if (filteredValues.includes(n.value)) {
            n.checked = false
          }
        })
      }
    }
  } else {
    const checkedNodeList = checkNodes.filter((item: any) => !(item.parent && item.parent.checked))
    const newList = checkedNodeList.map((item: any) => item.value)
    props.model[props.prop] = newList
  }
}

function handleVisibleChange() {
  if (props.areaStrictly) {
    const checkNodes: any[] = cascaderRef.value.getCheckedNodes()
    innerModelValue.value = checkNodes.map(it => it.pathValues)
  }
}

function getCascadeNodes(node: any, filter?: NodeFilter) {
  const aNodes = getAncestralNodes(node, filter)
  const dNodes = getDescendantNodes(node, filter)

  return [...aNodes, ...dNodes]
}

/** 获取祖先节点 */
function getAncestralNodes(node: any, filter?: NodeFilter, acc: any[] = []) {
  if (!node.parent) return acc

  const n = node.parent

  if (!filter || (filter && filter(n))) acc.push(n)
  getAncestralNodes(n, filter, acc)

  return acc
}

/** 获取子孙节点 */

function getDescendantNodes(node: any, filter?: NodeFilter, acc: any[] = []) {
  if (!node.children?.length) return acc

  node.children.forEach((n: any) => {
    if (!filter || (filter && filter(n))) acc.push(n)
    getDescendantNodes(n, filter, acc)
  })

  return acc
}

/** 深度处理 */
function deepProcessNodes(node: any, parent: any, fn: any) {
  if (!node) return

  fn(node, parent)

  if (!node.children?.length) return

  node.children.forEach((sn: any) => {
    deepProcessNodes(sn, node, fn)
  })
}

function nodeValue(v: any) {
  if (Array.isArray(v)) return v[v.length - 1]
  return v
}
</script>

<style scoped>
.example-block {
  margin: 1rem;
}
.example-demonstration {
  margin: 1rem;
}
</style>
